/**
 * LeadVendorForm.jsx — Add / Edit Lead Vendor
 *
 * Full Ringy-parity form matching the spec screenshots.
 * Sections: Basic info → Distribution → First touch → Field Mapping → Report Settings
 */

import AttachBusinessCardToggle from '../components/businessCard/AttachBusinessCardToggle.jsx';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Users, Map, BarChart2, Info,
  AlertTriangle, Copy, Check, ChevronDown, ChevronUp, X, Plus,
  CheckCircle2, Clock, Trash2, KeyRound, RefreshCw,
  MessageSquareText, Bot, Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost, apiPut } from '../lib/api.js';
import DistributionSection from './leadVendor/DistributionSection.jsx';
import CostSettingsSection from './leadVendor/CostSettingsSection.jsx';
import { costDefaults, hydrateCost, buildCostFields } from './leadVendor/costState.js';
import { distributionDefaults, hydrateDistribution, buildDistributionFields } from './leadVendor/distributionState.js';

// ─── Constants ─────────────────────────────────────────────────────────────────

// CIVIX color swatches for the vendor color picker (palette + complements).
const COLOR_SWATCHES = [
  '#7C3AED', // CIVIX purple
  '#E040FB', // CIVIX magenta
  '#3B82F6', // CIVIX blue
  '#22D3EE', // cyan
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#EC4899', // pink
];

const STANDARD_FIELDS = [
  { key: 'phone',          label: 'Phone number',    type: 'phone',   required: true  },
  { key: 'firstName',      label: 'First name',      type: 'string',  required: false },
  { key: 'lastName',       label: 'Last name',       type: 'string',  required: false },
  { key: 'fullName',       label: 'Full name',       type: 'string',  required: false },
  { key: 'email',          label: 'Email address',   type: 'email',   required: false },
  { key: 'state',          label: 'State',           type: 'string',  required: false },
  { key: 'city',           label: 'City',            type: 'string',  required: false },
  { key: 'zip',            label: 'ZIP code',        type: 'string',  required: false },
  { key: 'productInterest',label: 'Product interest',type: 'string',  required: false },
  { key: 'age',            label: 'Age',             type: 'number',  required: false },
  { key: 'ageRange',       label: 'Age range',       type: 'string',  required: false },
  { key: 'beneficiary',    label: 'Beneficiary',     type: 'string',  required: false },
  { key: 'existingLifeInsurance', label: 'Existing life insurance', type: 'string', required: false },
  { key: 'dob',            label: 'Date of birth',   type: 'date',    required: false },
];

// Same default set used by the backend on new-vendor create. Mirrors the
// Ringy default chip selection so a freshly-saved vendor doesn't immediately
// trip the "you only selected Phone number" warning banner.
function DEFAULT_NEW_VENDOR_FIELDS() {
  return [
    { key: 'phone',                 label: 'Phone number',            type: 'phone',  required: true,  customField: false },
    { key: 'fullName',              label: 'Full name',               type: 'string', required: false, customField: false },
    { key: 'email',                 label: 'Email',                   type: 'email',  required: false, customField: false },
    { key: 'state',                 label: 'State',                   type: 'string', required: false, customField: false },
    { key: 'ageRange',              label: 'Age range',               type: 'string', required: false, customField: false },
    { key: 'beneficiary',           label: 'Beneficiary',             type: 'string', required: false, customField: false },
    { key: 'existingLifeInsurance', label: 'Existing life insurance', type: 'string', required: false, customField: false },
  ];
}

// ─── Empty form state ──────────────────────────────────────────────────────────

function emptyForm() {
  return {
    name: '',
    vendorEmail: '',
    emailApiKeyOnCreate: false,
    color: '#7C3AED',
    defaultCostPerLead: '',
    ...costDefaults(),
    processNewLeadDelay: '',
    distribution: {
      // Round-robin is the default for vendors added through this form (PR
      // #435 / #422). Automated importers keep the model's `account` default.
      mode: 'roundRobin',
      config: {},
      singleAgentId: '', // target for `singleAgent` mode
      rules: { onlyMasterSwitchOn: true, onlyActivelyWorking: false },
      filters: { rejectStates: [], rejectAgeMin: '', rejectAgeMax: '' },
    },
    fieldMapping: {
      processVia: 'api',
      fields: DEFAULT_NEW_VENDOR_FIELDS(),
      saveEmptyFields: false,
    },
    reportSettings: {
      leadStatusGroupId: '',
      allowMembersToLink: false,
      linkedToVendorId: '',
    },
    dripGroupIds: [], // MarketingDripGroup ids to auto-enroll new leads in
    // PR #422 — round-robin agent pool (Agent ids) + Meta Instant Form routing.
    agentPool: [],
    ...distributionDefaults(),
    metaFormId: '',
    // Retained in the payload for backward compat, but no longer surfaced in the
    // UI: it no longer triggers a new-lead send (the first-touch text is driven
    // by autoOutbound.mode==='ai_text_bot' + autoOutbound.textBotId).
    defaultTextBot: '',
    // Append the assigned agent's Digital Business Card link to the first-touch text.
    attachDigitalBusinessCard: true,
    // First-touch behavior when a lead arrives. Drip fires off the canonical
    // top-level dripGroupIds; ai_text_bot fires off textBotId + delayMinutes.
    autoOutbound: {
      mode: 'off',          // 'off' | 'drip' | 'ai_text_bot'
      textBotId: '',        // required when mode === 'ai_text_bot'
      delayMinutes: 3,      // 0-15; TCPA safe-harbor + dedup + correction window
    },
  };
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function LeadVendorForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams(); // present when editing
  const isEdit = !!id;

  const [form, setForm]         = useState(emptyForm());
  const [errors, setErrors]     = useState({});
  const [busy, setBusy]         = useState(false);
  /** Synchronous double-submit guard; `busy` state lags a render behind. */
  const busyRef = useRef(false);
  const [showColor, setShowColor] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fieldSearch, setFieldSearch] = useState('');
  const [showFieldDrop, setShowFieldDrop] = useState(false);
  const fieldDropRef = useRef(null);

  // Load existing vendor for editing
  const { data: editData } = useQuery({
    queryKey: ['lead-vendor', id],
    queryFn: () => apiGet(`/lead-vendors/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (editData?.vendor) {
      const v = editData.vendor;
      const hydratedDripIds = (v.dripGroupIds || []).map((g) => (g && g._id ? String(g._id) : String(g)));
      setForm({
        name:                v.name || '',
        vendorEmail:         v.vendorEmail || v.contactEmail || '',
        emailApiKeyOnCreate: v.emailApiKeyOnCreate || false,
        color:               v.color || '#7C3AED',
        defaultCostPerLead:  v.defaultCostPerLead != null ? String(v.defaultCostPerLead) : '',
        ...hydrateCost(v),
        processNewLeadDelay: v.processNewLeadDelay != null ? String(v.processNewLeadDelay) : '',
        distribution: {
          // Preserve the vendor's persisted mode; legacy vendors saved before
          // PR #435 keep their `account`/`distributeAgents`/etc. value untouched.
          mode:    v.distribution?.mode || 'account',
          config:  v.distribution?.config || {},
          singleAgentId: v.distribution?.singleAgentId
                          ? (v.distribution.singleAgentId._id
                              ? String(v.distribution.singleAgentId._id)
                              : String(v.distribution.singleAgentId))
                          : '',
          rules: {
            onlyMasterSwitchOn:  v.distribution?.rules?.onlyMasterSwitchOn !== false,
            onlyActivelyWorking: !!v.distribution?.rules?.onlyActivelyWorking,
          },
          filters: {
            rejectStates: Array.isArray(v.distribution?.filters?.rejectStates) ? v.distribution.filters.rejectStates : [],
            rejectAgeMin: v.distribution?.filters?.rejectAgeMin ?? '',
            rejectAgeMax: v.distribution?.filters?.rejectAgeMax ?? '',
          },
        },
        fieldMapping: {
          processVia:      v.fieldMapping?.processVia || 'api',
          fields:          v.fieldMapping?.fields?.length ? v.fieldMapping.fields : DEFAULT_NEW_VENDOR_FIELDS(),
          saveEmptyFields: v.fieldMapping?.saveEmptyFields || false,
        },
        reportSettings: {
          leadStatusGroupId: v.reportSettings?.leadStatusGroupId || '',
          allowMembersToLink: v.reportSettings?.allowMembersToLink || false,
          linkedToVendorId:   v.reportSettings?.linkedToVendorId || '',
        },
        agentPool: (v.agentPool || []).map((a) => (a && a._id ? String(a._id) : String(a))),
        ...hydrateDistribution(v),
        metaFormId: v.metaFormId || '',
        // dripGroupIds may come back as either raw ObjectId strings or
        // populated { _id, name, trigger } shapes — normalise to string ids
        // so the picker checkboxes work consistently. This is the CANONICAL
        // drip field the runtime consumes (enrollLeadInVendorDrips).
        dripGroupIds: hydratedDripIds,
        // defaultTextBot is retained in the payload (non-destructive) but no
        // longer surfaced in the UI — it no longer triggers a new-lead send.
        defaultTextBot: v.defaultTextBot
                          ? (v.defaultTextBot._id ? String(v.defaultTextBot._id) : String(v.defaultTextBot))
                          : '',
        attachDigitalBusinessCard: v.attachDigitalBusinessCard !== false,
        autoOutbound: {
          // Derive the displayed first-touch choice. A legacy vendor may have
          // mode==='off' yet still fire drips off the top-level dripGroupIds
          // (the runtime is mode-agnostic for drips), so surface it as Drip —
          // otherwise the form would silently claim "Off" for a vendor that
          // actually drips.
          mode:         v.autoOutbound?.mode === 'ai_text_bot'
                          ? 'ai_text_bot'
                          : (hydratedDripIds.length > 0 ? 'drip' : 'off'),
          textBotId:    v.autoOutbound?.textBotId
                          ? (v.autoOutbound.textBotId._id
                              ? String(v.autoOutbound.textBotId._id)
                              : String(v.autoOutbound.textBotId))
                          : '',
          delayMinutes: Number.isFinite(v.autoOutbound?.delayMinutes)
                          ? v.autoOutbound.delayMinutes
                          : 3,
        },
      });
    }
  }, [editData]);

  // Load dropdown data
  const { data: statusGroupsData } = useQuery({
    queryKey: ['lead-status-groups'],
    queryFn: () => apiGet('/lead-status-groups'),
  });
  const { data: vendorsData } = useQuery({
    queryKey: ['lead-vendors-list'],
    queryFn: () => apiGet('/lead-vendors'),
  });
  // Drip Builder groups with trigger='new_lead' — the only kind eligible
  // for vendor auto-enrollment. Empty list → render the empty-state hint.
  const { data: dripOptionsData } = useQuery({
    queryKey: ['lead-vendor-drip-options'],
    queryFn: () => apiGet('/lead-vendors/drip-options'),
  });
  const dripOptions = dripOptionsData?.options || [];
  // PR #385 — text bots for the autoOutbound picker. Silent-fail on 401/500;
  // the picker just shows an empty state so the form still submits.
  const { data: textBotsData } = useQuery({
    queryKey: ['text-bots-list'],
    queryFn: () => apiGet('/text-bots').catch(() => ({ bots: [] })),
  });
  const textBotOptions = (textBotsData?.bots || textBotsData?.items || [])
    .filter((b) => !b.archived);
  const statusGroups     = statusGroupsData?.items || [];
  const otherVendors     = (vendorsData?.vendors || []).filter((v) => v._id !== id);
  const vendorRaw        = editData?.vendor || null;

  // PR #422 — all tenant agents for the round-robin pool chip picker.
  const { data: tenantAgentsData } = useQuery({
    queryKey: ['lead-vendor-tenant-agents'],
    queryFn: () => apiGet('/agents').catch(() => ({ agents: [] })),
  });
  const tenantAgents = tenantAgentsData?.agents || [];

  // Close field dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (fieldDropRef.current && !fieldDropRef.current.contains(e.target)) {
        setShowFieldDrop(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ─── Validation ───────────────────────────────────────────────────────────

  function validate(f = form) {
    const errs = {};
    if (!f.name.trim()) errs.name = 'Vendor name is required';
    if (f.vendorEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.vendorEmail)) {
      errs.vendorEmail = 'Invalid email address';
    }
    if (f.emailApiKeyOnCreate && !f.vendorEmail.trim()) {
      errs.vendorEmail = 'Enter a valid email address first';
    }
    return errs;
  }

  const isValid = Object.keys(validate()).length === 0;

  function setField(path, value) {
    setForm((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  }

  // ─── Field mapping helpers ─────────────────────────────────────────────────

  const selectedFieldKeys = form.fieldMapping.fields.map((f) => f.key);
  const onlyPhoneSelected = selectedFieldKeys.length === 1 && selectedFieldKeys[0] === 'phone';

  const filteredFields = STANDARD_FIELDS.filter(
    (f) => f.key !== 'phone' &&
      f.label.toLowerCase().includes(fieldSearch.toLowerCase()) &&
      !selectedFieldKeys.includes(f.key)
  );

  function addField(field) {
    setForm((prev) => ({
      ...prev,
      fieldMapping: {
        ...prev.fieldMapping,
        fields: [...prev.fieldMapping.fields, { ...field, customField: false }],
      },
    }));
  }

  // Slugify a label into a field key: lowercase, spaces → _, strip anything
  // outside [a-z0-9_]. Mirrors the LeadVendor model's server-side helper.
  function slugifyFieldKey(label) {
    return String(label)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  }

  // Create a NEW custom field from free-typed search text. Unlike addField
  // (which forces customField:false for the STANDARD_FIELDS presets), this
  // preserves customField:true so the arbitrary payload key round-trips.
  function addCustomField(label) {
    const trimmed = String(label).trim();
    const key = slugifyFieldKey(trimmed);
    if (!key) return;
    // Already selected → no-op (never duplicate a chip).
    if (selectedFieldKeys.includes(key)) return;
    // Collides with a known standard field → add that preset instead.
    const std = STANDARD_FIELDS.find((f) => f.key === key);
    if (std) { addField(std); return; }
    setForm((prev) => ({
      ...prev,
      fieldMapping: {
        ...prev.fieldMapping,
        fields: [
          ...prev.fieldMapping.fields,
          { key, label: trimmed, type: 'string', required: false, customField: true },
        ],
      },
    }));
  }

  function removeField(key) {
    if (key === 'phone') return; // phone is always required
    setForm((prev) => ({
      ...prev,
      fieldMapping: {
        ...prev.fieldMapping,
        fields: prev.fieldMapping.fields.filter((f) => f.key !== key),
      },
    }));
  }

  function toggleDripGroup(groupId) {
    setForm((prev) => {
      const ids = new Set((prev.dripGroupIds || []).map(String));
      const key = String(groupId);
      if (ids.has(key)) ids.delete(key); else ids.add(key);
      return { ...prev, dripGroupIds: Array.from(ids) };
    });
  }

  // Single "What happens when a lead arrives" choice. Because the runtime fires
  // drips off the TOP-LEVEL dripGroupIds regardless of autoOutbound.mode (see
  // enrollLeadInVendorDrips), a mere mode flip would not actually stop a drip.
  // So this makes the choice mutually exclusive by clearing the other branch's
  // inputs — but ONLY on an explicit user click (never on hydration), so opening
  // an existing vendor never silently wipes its saved config.
  function selectFirstTouchMode(mode) {
    setForm((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next.autoOutbound.mode = mode;
      if (mode === 'off') {
        next.dripGroupIds = [];
        next.autoOutbound.textBotId = '';
      } else if (mode === 'drip') {
        // Drip uses the canonical top-level dripGroupIds; no bot should fire.
        next.autoOutbound.textBotId = '';
      } else if (mode === 'ai_text_bot') {
        // Bot-only first touch — drips would otherwise ALSO fire (mode-agnostic).
        next.dripGroupIds = [];
      }
      return next;
    });
  }

  // Prefill this vendor's config from an existing vendor's settings. Copies
  // the whole configuration surface (distribution, contact, field mapping,
  // report, auto-outbound, drips, agent pool) but never the source vendor's
  // identity or credentials (name, email, API key/SID, Meta form id) — those
  // stay unique to this vendor. The source vendor is fetched from the tenant-
  // scoped endpoint, so only the current tenant's vendors are ever copyable.
  async function copyFromVendor(sourceVendorId) {
    try {
      const r = await apiGet(`/lead-vendors/${sourceVendorId}`);
      const v = r?.vendor;
      if (!v) { toast.error('Failed to copy vendor settings'); return; }
      setForm((prev) => ({
        ...prev,
        color:               v.color || prev.color,
        defaultCostPerLead:  v.defaultCostPerLead != null ? String(v.defaultCostPerLead) : prev.defaultCostPerLead,
        ...hydrateCost(v),
        processNewLeadDelay: v.processNewLeadDelay != null ? String(v.processNewLeadDelay) : prev.processNewLeadDelay,
        distribution: {
          mode:    v.distribution?.mode || prev.distribution.mode,
          config:  v.distribution?.config || {},
          singleAgentId: v.distribution?.singleAgentId
                          ? (v.distribution.singleAgentId._id
                              ? String(v.distribution.singleAgentId._id)
                              : String(v.distribution.singleAgentId))
                          : '',
          rules: {
            onlyMasterSwitchOn:  v.distribution?.rules?.onlyMasterSwitchOn !== false,
            onlyActivelyWorking: !!v.distribution?.rules?.onlyActivelyWorking,
          },
          filters: {
            rejectStates: Array.isArray(v.distribution?.filters?.rejectStates) ? v.distribution.filters.rejectStates : [],
            rejectAgeMin: v.distribution?.filters?.rejectAgeMin ?? '',
            rejectAgeMax: v.distribution?.filters?.rejectAgeMax ?? '',
          },
        },
        fieldMapping: {
          processVia:      v.fieldMapping?.processVia || 'api',
          fields:          v.fieldMapping?.fields?.length ? v.fieldMapping.fields : prev.fieldMapping.fields,
          saveEmptyFields: v.fieldMapping?.saveEmptyFields || false,
        },
        reportSettings: {
          leadStatusGroupId:  v.reportSettings?.leadStatusGroupId || '',
          allowMembersToLink: v.reportSettings?.allowMembersToLink || false,
          linkedToVendorId:   v.reportSettings?.linkedToVendorId || '',
        },
        agentPool: (v.agentPool || []).map((a) => (a && a._id ? String(a._id) : String(a))),
        ...hydrateDistribution(v),
        dripGroupIds: (v.dripGroupIds || []).map((g) => (g && g._id ? String(g._id) : String(g))),
      }));
      toast.success('Vendor settings copied');
    } catch { toast.error('Failed to copy vendor settings'); }
  }

  // Normalized `distribution` sub-payload — shared by the main SAVE submit and
  // the add-agent flow so persisting the mode never drops or corrupts sibling
  // distribution fields (config.method, rules, filters, singleAgentId).
  function buildDistributionPayload() {
    const f = form.distribution.filters || {};
    return {
      ...form.distribution,
      singleAgentId: form.distribution.singleAgentId === '' ? null : form.distribution.singleAgentId,
      filters: {
        rejectStates: f.rejectStates || [],
        rejectAgeMin: f.rejectAgeMin === '' || f.rejectAgeMin == null ? null : parseInt(f.rejectAgeMin, 10),
        rejectAgeMax: f.rejectAgeMax === '' || f.rejectAgeMax == null ? null : parseInt(f.rejectAgeMax, 10),
      },
    };
  }

  // ─── Assignments handlers (server-backed) ───────────────────────────────
  async function refreshVendor() {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['lead-vendor', id] }),
    ]);
  }

  async function regenerateKeys() {
    if (!window.confirm('Rotating credentials will break any vendor still using the old keys. Continue?')) return;
    try {
      await apiPost(`/lead-vendors/${id}/regenerate-key`, {});
      toast.success('Credentials rotated');
      await refreshVendor();
    } catch (err) { toast.error(err.response?.data?.error || err.message || 'Failed to rotate'); }
  }

  function copyToClipboard(text, label) {
    if (!text) return;
    navigator.clipboard?.writeText(String(text))
      .then(() => toast.success(`${label || 'Copied'}`))
      .catch(() => toast.error('Copy failed'));
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  async function submit(e) {
    e?.preventDefault?.();
    if (busyRef.current) return;
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error('Please fix form errors before saving');
      return;
    }

    busyRef.current = true;
    setBusy(true);
    setErrors({});
    try {
      // Optional ObjectId ref fields the user left blank are held as '' in form
      // state (Auto-Outbound off, "No status group", no link-to-vendor, etc.).
      // Mongoose can't cast '' to an ObjectId and 400s on save, so serialize
      // them as null. The backend schema also coerces '' → undefined, but
      // normalizing here keeps the wire payload clean.
      const nullIfEmpty = (v) => (v === '' ? null : v);

      const payload = {
        ...form,
        defaultTextBot: nullIfEmpty(form.defaultTextBot),
        defaultCostPerLead: form.defaultCostPerLead ? parseFloat(form.defaultCostPerLead) : 0,
        ...buildCostFields(form),
        processNewLeadDelay: form.processNewLeadDelay ? parseInt(form.processNewLeadDelay, 10) : 0,
        reportSettings: {
          ...form.reportSettings,
          leadStatusGroupId: nullIfEmpty(form.reportSettings.leadStatusGroupId),
          linkedToVendorId:  nullIfEmpty(form.reportSettings.linkedToVendorId),
        },
        distribution: { ...buildDistributionPayload(), mode: 'account' },
        ...buildDistributionFields(form),
      };

      if (isEdit) {
        await apiPut(`/lead-vendors/${id}`, payload);
        toast.success('Lead vendor updated');
      } else {
        await apiPost('/lead-vendors', payload);
        toast.success('Lead vendor created');
      }
      qc.invalidateQueries({ queryKey: ['lead-vendors'] });
      // Also drop the cached DETAIL query so reopening this vendor refetches
      // fresh — otherwise just-saved fields (e.g. custom field mappings) show
      // stale on reopen. Mirrors refreshVendor()'s ['lead-vendor', id] invalidation.
      if (isEdit && id) qc.invalidateQueries({ queryKey: ['lead-vendor', id] });
      navigate('/vendors');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Save failed';
      if (err.response?.status === 409) {
        setErrors((prev) => ({ ...prev, name: msg }));
      }
      toast.error(msg);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-16">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-civix-purpleSoft/80 uppercase tracking-[0.3em] text-xs">Lead Sources</div>
          <h1 className="font-display text-3xl font-bold mt-1">
            {isEdit ? 'Edit Lead Vendor' : 'Add Lead Vendor'}
          </h1>
        </div>
        <button
          onClick={() => navigate('/vendors')}
          className="flex items-center gap-1 text-sm text-white/60 hover:text-white transition"
        >
          <ArrowLeft className="w-4 h-4" />
          GO BACK
        </button>
      </div>

      {/* ── Verification banner + Integration credentials (edit mode only) ────── */}
      {isEdit && vendorRaw && (
        <VerifyBanner vendor={vendorRaw} />
      )}
      {isEdit && vendorRaw && (
        <IntegrationCard vendor={vendorRaw} onRotate={regenerateKeys} onCopy={copyToClipboard} />
      )}

      <form onSubmit={submit} className="space-y-5">

        {/* ── Top section: Basic Info ─────────────────────────────────────────── */}
        <div className="panel p-6 space-y-4">
          {/* Vendor Name */}
          <div>
            <label className="label required">Lead vendor name</label>
            <input
              className={`input mt-1 w-full ${errors.name ? 'border-red-500 focus:border-red-500' : ''}`}
              value={form.name}
              onChange={(e) => { setField('name', e.target.value); setErrors((p) => ({ ...p, name: undefined })); }}
              placeholder="e.g. LeadHeroes, SmartFinancial…"
            />
            {errors.name && <div className="text-red-400 text-xs mt-1">{errors.name}</div>}
          </div>

          {/* Vendor Email */}
          <div>
            <label className="label">Lead vendor email</label>
            <input
              className={`input mt-1 w-full ${errors.vendorEmail ? 'border-red-500 focus:border-red-500' : ''}`}
              type="email"
              value={form.vendorEmail}
              onChange={(e) => { setField('vendorEmail', e.target.value); setErrors((p) => ({ ...p, vendorEmail: undefined })); }}
              placeholder="vendor@example.com"
            />
            <div className="text-xs text-white/45 mt-1">
              *This is used to send your lead vendor their API key credentials and integration instructions.
            </div>
            {errors.vendorEmail && <div className="text-red-400 text-xs mt-1">{errors.vendorEmail}</div>}
          </div>

          {/* Email checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.emailApiKeyOnCreate}
              onChange={(e) => setField('emailApiKeyOnCreate', e.target.checked)}
              className="mt-0.5 accent-civix-purple"
            />
            <span className="text-sm text-white/75">
              Email lead vendor my API keys and integration instructions?{' '}
              <span className="text-white/45">(enter a valid email address above first)</span>
            </span>
          </label>

          {/* Color + Cost + Delay row */}
          <div className="flex flex-wrap items-end gap-4 pt-1">
            {/* Color swatches (CIVIX palette + custom) */}
            <div>
              <label className="label">Vendor color</label>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {COLOR_SWATCHES.map((c) => {
                  const selected = form.color?.toLowerCase() === c.toLowerCase();
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setField('color', c)}
                      title={c}
                      className={`w-7 h-7 rounded-full transition ${selected ? 'ring-2 ring-offset-2 ring-offset-civix-bg ring-white' : 'hover:scale-110'}`}
                      style={{ backgroundColor: c }}
                    />
                  );
                })}
                <button
                  type="button"
                  onClick={() => setShowColor(!showColor)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition ${
                    !COLOR_SWATCHES.includes(form.color?.toUpperCase())
                      ? 'border-civix-purpleSoft text-civix-purpleSoft bg-civix-purple/10'
                      : 'border-civix-border/60 text-white/70 hover:border-civix-purple/50'
                  }`}
                >
                  Custom
                </button>
                {showColor && (
                  <div className="absolute mt-12 z-30 bg-civix-panel border border-civix-border rounded-xl p-3 shadow-2xl flex items-center gap-2">
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setField('color', e.target.value)}
                      className="w-12 h-9 rounded-sm cursor-pointer"
                    />
                    <input
                      type="text"
                      value={form.color}
                      onChange={(e) => setField('color', e.target.value)}
                      className="input py-1 text-xs w-24"
                      placeholder="#7C3AED"
                    />
                    <button type="button" onClick={() => setShowColor(false)} className="btn-ghost text-xs">Done</button>
                  </div>
                )}
              </div>
            </div>

            {/* Process delay */}
            <div className="flex-1 min-w-[140px]">
              <label className="label">Process new lead delay</label>
              <div className="relative mt-1">
                <input
                  className="input w-full"
                  type="number"
                  min="0"
                  value={form.processNewLeadDelay}
                  onChange={(e) => setField('processNewLeadDelay', e.target.value)}
                  placeholder="0"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-xs">sec</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Round-robin agent pool + Meta form routing (PR #422) ──────────── */}
        <div className="panel p-6 space-y-4">
          <SectionHeader icon={Users} title="Meta lead routing" />

          {/* Meta Instant Form ID — webhook routing key */}
          <div>
            <label className="label">Meta Instant Form ID</label>
            <input
              className="input mt-1 w-full"
              value={form.metaFormId}
              onChange={(e) => setField('metaFormId', e.target.value)}
              placeholder="e.g. 1234567890123456"
            />
            <div className="text-xs text-white/45 mt-1">
              Leads submitted to this Meta Instant Form are routed to this vendor via the
              <code className="mx-1 text-white/60">/api/webhooks/meta/leads</code> webhook.
            </div>
          </div>

        </div>

        {/* ── Distribution (self | weighted round-robin) ── */}
        <CostSettingsSection form={form} setField={setField} />

        <DistributionSection form={form} setField={setField} tenantAgents={tenantAgents} />

        {/* ── What happens when a lead arrives ──────────────────────────────
            One first-touch choice: Off / Drip / AI Text Bot. Consolidates the
            former Auto-Enroll Drips, Default AI Text Bot, and Auto-Outbound
            panels. Drip binds the CANONICAL top-level dripGroupIds (the field
            the runtime actually consumes); AI Text Bot binds autoOutbound
            textBotId + delayMinutes. selectFirstTouchMode enforces mutual
            exclusion on explicit click. */}
        <div className="panel p-6" data-testid="first-touch-section">
          <SectionHeader icon={Zap} title="What happens when a lead arrives" />
          <p className="text-sm text-white/60 mt-2">
            Choose the first-touch behavior for new leads from this vendor:
            <span className="text-civix-purpleSoft"> Off</span> (agents handle it),
            <span className="text-civix-purpleSoft"> Drip</span> (start a marketing sequence), or
            <span className="text-civix-purpleSoft"> AI Text Bot</span> (the bot texts the lead first,
            escalates to the assigned agent after 5 exchanges).
          </p>

          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            {[
              { value: 'off',         label: 'Off',         icon: X,                 desc: 'Do nothing on intake' },
              { value: 'drip',        label: 'Drip',        icon: MessageSquareText, desc: 'Auto-enroll in a drip' },
              { value: 'ai_text_bot', label: 'AI Text Bot', icon: Bot,               desc: 'Bot texts the lead first' },
            ].map((opt) => {
              const active = form.autoOutbound?.mode === opt.value;
              const Icon = opt.icon;
              return (
                <button
                  type="button"
                  key={opt.value}
                  data-testid={`autooutbound-mode-${opt.value}`}
                  onClick={() => selectFirstTouchMode(opt.value)}
                  className={`flex flex-col items-start gap-1 p-3 rounded border text-left transition ${
                    active
                      ? 'border-civix-purple bg-civix-purple/20 text-white'
                      : 'border-white/10 bg-white/5 text-white/70 hover:border-white/30'
                  }`}
                >
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Icon className="w-4 h-4" /> {opt.label}
                  </div>
                  <div className="text-xs text-white/60">{opt.desc}</div>
                </button>
              );
            })}
          </div>

          {form.autoOutbound?.mode !== 'off' && (
            <AttachBusinessCardToggle
              className="mt-4"
              id="vendor-attach-business-card"
              checked={form.attachDigitalBusinessCard}
              onChange={(v) => setField('attachDigitalBusinessCard', v)}
            />
          )}

          {form.autoOutbound?.mode === 'drip' && (
            <div className="mt-4" data-testid="first-touch-drip">
              <label className="label">Which drip(s) should start on intake?</label>
              <p className="text-xs text-white/50 mt-1">
                Only drips with the <span className="text-civix-purpleSoft">"New Lead"</span> trigger are eligible.
              </p>
              {dripOptions.length === 0 ? (
                <div
                  className="mt-2 text-sm text-amber-300/80 bg-amber-500/10 border border-amber-500/30 rounded-sm p-3"
                  data-testid="drip-picker-empty"
                >
                  No 'New Lead' drips found. Create one in Marketing → Drip Builder and set Trigger = New Lead.
                </div>
              ) : (
                <div className="mt-2 space-y-2" data-testid="drip-picker-list">
                  {dripOptions.map((g) => {
                    const selected = (form.dripGroupIds || []).map(String).includes(String(g._id));
                    return (
                      <label
                        key={g._id}
                        className="flex items-center gap-3 cursor-pointer p-2 rounded-sm hover:bg-white/5"
                        data-testid={`drip-option-${g._id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleDripGroup(g._id)}
                          className="accent-civix-purple"
                        />
                        <span className="text-sm text-white/80">{g.name}</span>
                        {g.vertical && (
                          <span className="ml-auto text-xs text-white/40 uppercase tracking-wider">{g.vertical}</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
              {(form.dripGroupIds || []).length > 0 && dripOptions.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="drip-picker-chips">
                  {(form.dripGroupIds || []).map((gid) => {
                    const opt = dripOptions.find((o) => String(o._id) === String(gid));
                    if (!opt) return null;
                    return (
                      <span
                        key={gid}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-civix-purple/20 text-civix-purpleSoft border border-civix-purple/40"
                      >
                        {opt.name}
                        <button
                          type="button"
                          onClick={() => toggleDripGroup(gid)}
                          className="ml-1 hover:text-white"
                          aria-label={`Remove ${opt.name}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {form.autoOutbound?.mode === 'ai_text_bot' && (
            <div className="mt-4 space-y-3" data-testid="first-touch-ai-text-bot">
              <div>
                <label className="label">Which text bot handles the first message?</label>
                {textBotOptions.length === 0 ? (
                  <div
                    className="mt-1 text-sm text-amber-300/80 bg-amber-500/10 border border-amber-500/30 rounded-sm p-3"
                    data-testid="autooutbound-textbot-empty"
                  >
                    No trained text bots found. Create one in AI Text Agent → Bots first.
                  </div>
                ) : (
                  <select
                    className="input mt-1 w-full"
                    data-testid="autooutbound-textbot-select"
                    value={form.autoOutbound.textBotId || ''}
                    onChange={(e) => setField('autoOutbound.textBotId', e.target.value)}
                  >
                    <option value="">Select a text bot…</option>
                    {textBotOptions.map((b) => (
                      <option key={b._id} value={b._id}>{b.name}</option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="label flex items-center justify-between">
                  <span>Delay before first text</span>
                  <span className="text-xs text-white/60">{form.autoOutbound.delayMinutes} min</span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={15}
                  step={1}
                  value={form.autoOutbound.delayMinutes ?? 3}
                  onChange={(e) => setField('autoOutbound.delayMinutes', Number(e.target.value))}
                  className="w-full accent-civix-purple mt-2"
                  data-testid="autooutbound-delay-slider"
                />
                <p className="text-xs text-white/50 mt-1">
                  3 minutes is recommended. It absorbs vendor re-posts, gives admins
                  a correction window, and keeps outbound comfortably inside TCPA
                  guardrails.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ── Field Mapping ─────────────────────────────────────────────────── */}
        {/* While the field-search dropdown is open, elevate this whole card's
            stacking context so its (downward-opening) dropdown paints ABOVE the
            following sibling section cards (e.g. Report settings). `.panel` has
            no position of its own, so `relative` is required for z-index. */}
        <div className={`panel p-6 relative ${showFieldDrop ? 'z-40' : ''}`}>
          <div className="flex items-start justify-between">
            <SectionHeader icon={Map} title="Lead field mapping" />
            {otherVendors.length > 0 && (
              <CopyFromVendorButton vendors={otherVendors} onCopy={copyFromVendor} />
            )}
          </div>

          <div className="mt-4 space-y-4">
            {/* Process via */}
            <div>
              <label className="label">Process leads through</label>
              <div className="flex gap-4 mt-2">
                {[{ v: 'api', l: 'API (default & recommended)' }, { v: 'email', l: 'Email' }].map(({ v, l }) => (
                  <label key={v} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="process-via"
                      value={v}
                      checked={form.fieldMapping.processVia === v}
                      onChange={() => setField('fieldMapping.processVia', v)}
                      className="accent-civix-purple"
                    />
                    <span className="text-sm text-white/80">{l}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Field selector */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label">Lead vendor fields</label>
                <span className="text-[10px] text-white/40 flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Create custom fields in Settings
                </span>
              </div>

              {/* Chips */}
              <div
                ref={fieldDropRef}
                className="relative"
              >
                <div
                  className="min-h-[42px] border border-civix-border rounded-lg p-2 flex flex-wrap gap-1.5 cursor-text bg-civix-bg/40 hover:border-civix-purple/50 transition"
                  onClick={() => setShowFieldDrop(true)}
                >
                  {form.fieldMapping.fields.map((f) => (
                    <span
                      key={f.key}
                      className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: f.key === 'phone' ? '#7c3aed33' : '#1e1e3a', color: f.key === 'phone' ? '#a78bfa' : '#c4b5fd' }}
                    >
                      {f.label || f.key}
                      {f.key !== 'phone' && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeField(f.key); }}
                          className="ml-0.5 text-white/40 hover:text-white"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  ))}
                  <input
                    className="flex-1 min-w-[120px] bg-transparent outline-hidden text-sm text-white placeholder-white/30 px-1"
                    placeholder="Search fields…"
                    name="vendor-field-search"
                    autoComplete="off"
                    value={fieldSearch}
                    onFocus={() => setShowFieldDrop(true)}
                    onChange={(e) => setFieldSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {showFieldDrop && (
                  <div className="absolute top-full mt-1 left-0 right-0 z-30 bg-civix-panel border border-civix-border rounded-lg shadow-2xl overflow-hidden">
                    {filteredFields.length === 0 && fieldSearch.trim() && (
                      <button
                        type="button"
                        onClick={() => { addCustomField(fieldSearch); setFieldSearch(''); setShowFieldDrop(false); }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-civix-purpleSoft/10 text-civix-purple flex items-center gap-2"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add custom field &ldquo;{fieldSearch.trim()}&rdquo;
                      </button>
                    )}
                    {filteredFields.length === 0 && !fieldSearch.trim() && (
                      <div className="p-3 text-xs text-white/45">
                        All standard fields added
                      </div>
                    )}
                    {filteredFields.map((f) => (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => { addField(f); setFieldSearch(''); setShowFieldDrop(false); }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-civix-purpleSoft/10 text-white/80 flex items-center justify-between"
                      >
                        <span>{f.label}</span>
                        <span className="text-xs text-white/40">{f.type}</span>
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setShowFieldDrop(false)}
                      className="w-full text-left px-4 py-2 text-xs text-white/40 hover:bg-white/5 border-t border-civix-border/40"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>

              {/* Save empty fields */}
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.fieldMapping.saveEmptyFields}
                  onChange={(e) => setField('fieldMapping.saveEmptyFields', e.target.checked)}
                  className="accent-civix-purple"
                />
                <span className="text-sm text-white/75 flex items-center gap-1">
                  Save all fields on leads even if they're empty
                  <Info className="w-3.5 h-3.5 text-white/40 cursor-help" title="When unchecked, null/empty values from the vendor payload are not stored." />
                </span>
              </label>

              {/* Warning: only phone selected */}
              {onlyPhoneSelected && (
                <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex gap-2 text-sm text-amber-200">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
                  <span>
                    <strong>Lead vendor fields not set</strong> — You only selected{' '}
                    <em>"Phone number"</em> under "Lead vendor fields". Your lead's name, location
                    and other information will not be processed.
                  </span>
                </div>
              )}
            </div>

            {/* Advanced settings toggle */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm text-civix-purpleSoft hover:text-white transition"
              >
                {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Show advanced settings
              </button>
              {showAdvanced && (
                <div className="mt-4 space-y-4 border-t border-civix-border/40 pt-4">
                  <p className="text-xs text-white/50">
                    Advanced field options — configure custom field types, required flags, and rejection rules.
                  </p>

                  {/* Rejection rules */}
                  <div className="rounded-lg bg-white/2.5 border border-civix-border/40 p-3 space-y-3">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-white/40">Rejection rules</div>
                    <div>
                      <label className="label text-xs">Reject leads from these states</label>
                      <input
                        className="input mt-1 w-full text-sm"
                        placeholder="e.g. CA, NY, MA"
                        value={(form.distribution.filters.rejectStates || []).join(', ')}
                        onChange={(e) => {
                          const list = e.target.value
                            .split(/[\s,]+/)
                            .map((s) => s.toUpperCase().trim())
                            .filter((s) => /^[A-Z]{2}$/.test(s));
                          setField('distribution.filters.rejectStates', list);
                        }}
                      />
                      <div className="text-xs text-white/45 mt-1">Comma-separated 2-letter US codes. Leads matching any of these states are dropped before assignment.</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="label text-xs">Reject if age below</label>
                        <input
                          type="number"
                          min="0"
                          className="input mt-1 w-full text-sm"
                          placeholder="e.g. 18"
                          value={form.distribution.filters.rejectAgeMin}
                          onChange={(e) => setField('distribution.filters.rejectAgeMin', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                        />
                      </div>
                      <div>
                        <label className="label text-xs">Reject if age above</label>
                        <input
                          type="number"
                          min="0"
                          className="input mt-1 w-full text-sm"
                          placeholder="e.g. 85"
                          value={form.distribution.filters.rejectAgeMax}
                          onChange={(e) => setField('distribution.filters.rejectAgeMax', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="text-[11px] uppercase tracking-[0.18em] text-white/40 pt-2">Field type editor</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase text-white/40 border-b border-civix-border/40">
                          <th className="py-2 text-left">Field Key</th>
                          <th className="py-2 text-left">Label</th>
                          <th className="py-2 text-left">Type</th>
                          <th className="py-2 text-center">Required</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.fieldMapping.fields.map((f, i) => (
                          <tr key={f.key} className="border-b border-civix-border/20">
                            <td className="py-2 pr-2 font-mono text-xs text-civix-cyan">{f.key}</td>
                            <td className="py-2 pr-2">
                              <input
                                className="input py-1 text-xs w-32"
                                value={f.label || f.key}
                                onChange={(e) => {
                                  const fields = [...form.fieldMapping.fields];
                                  fields[i] = { ...fields[i], label: e.target.value };
                                  setField('fieldMapping.fields', fields);
                                }}
                                disabled={f.key === 'phone'}
                              />
                            </td>
                            <td className="py-2 pr-2">
                              <select
                                className="input py-1 text-xs w-24"
                                value={f.type || 'string'}
                                onChange={(e) => {
                                  const fields = [...form.fieldMapping.fields];
                                  fields[i] = { ...fields[i], type: e.target.value };
                                  setField('fieldMapping.fields', fields);
                                }}
                                disabled={f.key === 'phone'}
                              >
                                {['string','phone','email','number','date','boolean'].map((t) => (
                                  <option key={t} value={t}>{t}</option>
                                ))}
                              </select>
                            </td>
                            <td className="py-2 text-center">
                              <input
                                type="checkbox"
                                checked={!!f.required}
                                onChange={(e) => {
                                  const fields = [...form.fieldMapping.fields];
                                  fields[i] = { ...fields[i], required: e.target.checked };
                                  setField('fieldMapping.fields', fields);
                                }}
                                className="accent-civix-purple"
                                disabled={f.key === 'phone'}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Report Settings ───────────────────────────────────────────────── */}
        <div className="panel p-6">
          <SectionHeader icon={BarChart2} title="Report settings" />
          <div className="space-y-4 mt-4">
            {/* Lead status group */}
            <div>
              <label className="label">Lead status group</label>
              <select
                className="input mt-1 w-full"
                value={form.reportSettings.leadStatusGroupId}
                onChange={(e) => setField('reportSettings.leadStatusGroupId', e.target.value)}
              >
                <option value="">No status group</option>
                {statusGroups.map((g) => (
                  <option key={g._id} value={g._id}>{g.name}</option>
                ))}
              </select>
              <div className="text-xs text-white/45 mt-1">
                A status group consists of a set of statuses assigned to leads after adding a disposition tag.
              </div>
            </div>

            {/* Allow members to link */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.reportSettings.allowMembersToLink}
                onChange={(e) => setField('reportSettings.allowMembersToLink', e.target.checked)}
                className="accent-civix-purple"
              />
              <span className="text-sm text-white/80">Allow company members to link to this lead vendor for reporting purposes</span>
            </label>

            {/* Link to vendor */}
            <div>
              <label className="label">Link this lead vendor to</label>
              <select
                className="input mt-1 w-full"
                value={form.reportSettings.linkedToVendorId}
                onChange={(e) => setField('reportSettings.linkedToVendorId', e.target.value)}
              >
                <option value="">No qualified lead vendors found</option>
                {otherVendors.map((v) => (
                  <option key={v._id} value={v._id}>{v.name}</option>
                ))}
              </select>
              <div className="text-xs text-white/45 mt-1">
                You can link this lead vendor to another vendor that belongs to your company admin for consolidated reporting.
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer actions ────────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/vendors')}
            className="btn-ghost"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isValid || busy}
            className={`px-8 py-2.5 rounded-lg font-bold text-sm tracking-wide transition ${
              isValid && !busy
                ? 'bg-civix-purple hover:bg-civix-purpleSoft text-white'
                : 'bg-white/10 text-white/30 cursor-not-allowed'
            }`}
          >
            {busy ? 'Saving…' : 'SAVE LEAD VENDOR'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 pb-1 border-b border-civix-border/40">
      <div className="w-7 h-7 rounded-md bg-civix-purple/20 flex items-center justify-center">
        <Icon className="w-4 h-4 text-civix-purpleSoft" />
      </div>
      <h3 className="font-display font-semibold text-base text-white">{title}</h3>
    </div>
  );
}

// ─── Verification banner (green when working, yellow when awaiting first lead) ─

function VerifyBanner({ vendor }) {
  const total       = vendor?.stats?.totalLeads ?? vendor?.stats?.totalDelivered ?? 0;
  const lastLeadAt  = vendor?.stats?.lastLeadAt ? new Date(vendor.stats.lastLeadAt) : null;
  const within30d   = lastLeadAt && (Date.now() - lastLeadAt.getTime()) < 30 * 24 * 3600 * 1000;
  const verified    = total > 0 && within30d;

  if (verified) {
    return (
      <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/8 p-4 flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-emerald-300">This lead vendor has been successfully verified and is working.</div>
          <div className="text-xs text-emerald-200/70 mt-0.5">
            {total.toLocaleString()} lead{total === 1 ? '' : 's'} received • Last lead {timeAgo(lastLeadAt)}.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/8 p-4 flex items-start gap-3">
      <Clock className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
      <div>
        <div className="font-semibold text-amber-300">Awaiting first lead</div>
        <div className="text-xs text-amber-200/70 mt-0.5">
          {total === 0 ? 'No leads received yet' : `Last lead was ${lastLeadAt ? timeAgo(lastLeadAt) : 'unknown'}`}. Once your vendor POSTs a test lead, this banner will turn green.
        </div>
      </div>
    </div>
  );
}

function timeAgo(d) {
  if (!d) return '';
  const diff = Math.max(0, Date.now() - d.getTime());
  const mins  = Math.floor(diff / 60_000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

// ─── Integration credentials card (Ringy-parity) ────────────────────────────

function IntegrationCard({ vendor, onRotate, onCopy }) {
  // Use VITE_PUBLIC_API_BASE if explicitly set (e.g. dev tunnels), otherwise
  // derive from the current origin so the snippet always points at the same
  // host the dashboard is loaded from — Cloudflare proxies /api/* to Railway.
  const envBase = import.meta.env?.VITE_PUBLIC_API_BASE;
  const baseUrl = (envBase && envBase.replace(/\/$/, ''))
    || (typeof window !== 'undefined' && window.location?.origin)
    || 'https://api.civixcrm.ai';
  const url = `${baseUrl}/api/public/leads/new-lead`;
  const sid       = vendor.sid       || '— (run backfill script)';
  const authToken = vendor.authToken || '— (run backfill script)';

  return (
    <div className="panel p-6 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-civix-purple/20 flex items-center justify-center">
            <KeyRound className="w-4 h-4 text-civix-purpleSoft" />
          </div>
          <h3 className="font-display font-semibold text-base text-white">Integration credentials</h3>
        </div>
        <button
          type="button"
          onClick={onRotate}
          className="flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Rotate
        </button>
      </div>

      <CredRow label="URL"          value={url}      onCopy={onCopy} />
      <CredRow label="Method"       value="POST"     plain />
      <CredRow label="Content-Type" value="application/json" plain />
      <CredRow label="SID"          value={sid}      onCopy={onCopy} mono />
      <CredRow label="Auth token"   value={authToken} onCopy={onCopy} mono secret />
      <div className="text-[11px] text-white/45 pt-1">
        Body field shape: <code className="text-civix-purpleSoft">{`{ sid, authToken, ...lead }`}</code>. Legacy <code>POST /api/lead-intake/{`{API_KEY}`}</code> still works for back-compat.
      </div>
    </div>
  );
}

function CredRow({ label, value, onCopy, plain, mono, secret }) {
  const [show, setShow] = React.useState(!secret);
  const display = secret && !show ? '•'.repeat(Math.min(32, String(value).length)) : value;
  return (
    <div className="flex items-center gap-2">
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/40 w-28 shrink-0">{label}</div>
      <div className={`flex-1 px-3 py-1.5 rounded-sm bg-civix-bg/60 border border-civix-border/40 text-sm break-all ${mono ? 'font-mono' : ''}`}>{display}</div>
      {secret && (
        <button type="button" onClick={() => setShow(!show)} className="text-xs text-white/50 hover:text-white">
          {show ? 'Hide' : 'Show'}
        </button>
      )}
      {!plain && (
        <button type="button" onClick={() => onCopy?.(value, `${label} copied`)} className="text-xs text-civix-purpleSoft hover:text-white p-1">
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function CopyFromVendorButton({ vendors, onCopy }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="px-3 py-1.5 rounded-sm text-xs font-bold bg-emerald-600/20 text-emerald-400 border border-emerald-600/30 hover:bg-emerald-600/30 transition"
      >
        COPY FROM ANOTHER VENDOR
      </button>
      {open && (
        <div className="absolute top-8 right-0 z-30 bg-civix-panel border border-civix-border rounded-lg shadow-2xl overflow-hidden w-52">
          {vendors.map((v) => (
            <button
              key={v._id}
              type="button"
              onClick={() => { onCopy(v._id); setOpen(false); }}
              className="w-full text-left px-4 py-2 text-sm hover:bg-civix-purpleSoft/10 text-white/80"
            >
              {v.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
